import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { audioManager } from '@/lib/audio/AudioManager';

export type Theme = 'light' | 'dark' | 'auto';

interface SettingsState {
  // Audio settings
  soundFX: boolean;
  volume: number;
  vibrate: boolean;

  // Gameplay settings
  hintsAlwaysOn: boolean;
  hideInvalidPicks: boolean;

  // UI settings
  theme: Theme;
  reducedMotion: boolean;
  highContrast: boolean;

  // Actions
  updateSettings: (settings: Partial<SettingsState>) => void;
  toggleSound: () => void;
  setVolume: (volume: number) => void;
  setTheme: (theme: Theme) => void;
  toggleHintsAlwaysOn: () => void;
  toggleHideInvalidPicks: () => void;
  toggleVibrate: () => void;
  toggleReducedMotion: () => void;
  toggleHighContrast: () => void;
}

const defaultSettings: Omit<SettingsState, keyof Pick<SettingsState, 'updateSettings' | 'toggleSound' | 'setVolume' | 'setTheme' | 'toggleHintsAlwaysOn' | 'toggleHideInvalidPicks' | 'toggleVibrate' | 'toggleReducedMotion' | 'toggleHighContrast'>> = {
  soundFX: true,
  volume: 0.8,
  vibrate: false,
  hintsAlwaysOn: false,
  hideInvalidPicks: false,
  theme: 'auto',
  reducedMotion: false,
  highContrast: false,
};

function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // Auto: follow system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,

      updateSettings: (settings: Partial<SettingsState>) => {
        set((state) => {
          const newState = { ...state, ...settings };

          // Apply theme immediately if changed
          if (settings.theme !== undefined) {
            applyTheme(settings.theme);
          }

          // Apply audio settings immediately
          if (settings.soundFX !== undefined) {
            audioManager.setEnabled(settings.soundFX);
          }
          if (settings.volume !== undefined) {
            audioManager.setVolume(settings.volume);
          }

          return newState;
        });
      },

      toggleSound: () => {
        set((state) => {
          const newSoundFX = !state.soundFX;
          audioManager.setEnabled(newSoundFX);
          return { soundFX: newSoundFX };
        });
      },

      setVolume: (volume: number) => {
        const clampedVolume = Math.max(0, Math.min(1, volume));
        set({ volume: clampedVolume });
        audioManager.setVolume(clampedVolume);
      },

      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
      },

      toggleHintsAlwaysOn: () => {
        set((state) => ({ hintsAlwaysOn: !state.hintsAlwaysOn }));
      },

      toggleHideInvalidPicks: () => {
        set((state) => ({ hideInvalidPicks: !state.hideInvalidPicks }));
      },

      toggleVibrate: () => {
        set((state) => ({ vibrate: !state.vibrate }));
      },

      toggleReducedMotion: () => {
        set((state) => ({ reducedMotion: !state.reducedMotion }));
      },

      toggleHighContrast: () => {
        set((state) => ({ highContrast: !state.highContrast }));
      },
    }),
    {
      name: 'pure-luck-settings',
      // Only persist settings, not actions
      partialize: (state) => ({
        soundFX: state.soundFX,
        volume: state.volume,
        vibrate: state.vibrate,
        hintsAlwaysOn: state.hintsAlwaysOn,
        hideInvalidPicks: state.hideInvalidPicks,
        theme: state.theme,
        reducedMotion: state.reducedMotion,
        highContrast: state.highContrast,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply settings when rehydrated
        if (state) {
          applyTheme(state.theme);
          audioManager.setEnabled(state.soundFX);
          audioManager.setVolume(state.volume);
        }
      },
    }
  )
);
